import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { useWhoami } from '../../../stores/org';
import { resolveArtifactLink, type ArtifactLinkTarget } from './artifact-links';
import { isPlainLeftClick } from './plain-left-click';

export type ArtifactLinkProps = Omit<
  React.ComponentPropsWithoutRef<'a'>,
  'href'
> & {
  /** A destination from the artifact-link registry — nothing else is linkable. */
  to: ArtifactLinkTarget;
  /**
   * Replace the top stack entry instead of stacking on it. Use when the link sits
   * inside a view of the same shape (run → run), so Back still returns to the
   * artifact rather than walking a chain of siblings.
   */
  replaceTop?: boolean;
};

/**
 * A link that stays inside the artifact panel when there is one.
 *
 * Inside a panel it pushes the destination onto the focused artifact's view
 * stack, so clicking a workflow run from a dataset's automations doesn't tear the
 * user out of the dataset they're reading. Anywhere else it's an ordinary
 * react-router `<Link>` to the destination's page.
 */
export const ArtifactLink: React.FC<ArtifactLinkProps> = ({
  to,
  replaceTop = false,
  onClick,
  children,
  ...anchorProps
}) => {
  const artifactPanel = useArtifactPanel();
  const orgSlug = useWhoami((s) => s.org.slug);
  const { href, entry } = useMemo(
    () => resolveArtifactLink(to, orgSlug),
    [to, orgSlug]
  );

  // `pushView` targets the focused artifact, so with no artifact open there is
  // nothing to push onto and the link has to navigate instead.
  const canPush =
    artifactPanel !== null && artifactPanel.activeArtifactId !== null;

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (
        event.defaultPrevented ||
        !isPlainLeftClick(event, anchorProps.target)
      ) {
        return;
      }
      event.preventDefault();
      artifactPanel?.pushView(entry, { replaceTop });
    },
    [onClick, anchorProps.target, artifactPanel, entry, replaceTop]
  );

  if (!canPush) {
    return (
      <Link to={href} onClick={onClick} {...anchorProps}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} onClick={handleClick} {...anchorProps}>
      {children}
    </a>
  );
};
